import { captureException, captureMessage } from "@sentry/browser";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import useAnalytics from "@/hooks/use-analytics";
import useAuth, { clearStaleTokens } from "@/hooks/use-auth";
import usePWA from "@/hooks/use-pwa";
import { exploreCategoryModal, type UserType, user } from "@/store";

import useGetOrCreateUser from "./hooks/use-get-or-create-user";
import SignUp from "./sign-up";
import { INVITE_CODE_STORAGE_KEY } from "./sign-up/username";
import WrapperModal from "./wrapper-modal";

export const STEPS = {
  DETAILS: 0,
  USERNAME: 1,
} as const;
export type STEPS = (typeof STEPS)[keyof typeof STEPS];

export default function AuthModal() {
  const [step, setStep] = useState<STEPS>(STEPS.USERNAME);
  const [isOpen, setIsOpen] = useState(false);

  const { identify, track } = useAnalytics();
  const { logout, authToken } = useAuth();

  const { isPWA } = usePWA();
  const search = useSearch({ strict: false }) as Record<
    string,
    string | undefined
  >;

  const [getUser] = useGetOrCreateUser();

  const identifyUser = (userData: UserType) => {
    identify(userData, isPWA);
  };

  const handleClose = (open: boolean) => {
    if (open) {
      return;
    }

    track("User Profile Set Up Cancelled");

    if (!user()?.isPublic) {
      logout();
    }

    setIsOpen(false);
  };

  const handleCompleteSignup = (userData: UserType) => {
    identifyUser(userData);
    track("User Profile Set Up");
    setIsOpen(false);
    exploreCategoryModal(true);
  };

  const handleUsername = () => setStep(STEPS.DETAILS);

  const handleAuthError = () => {
    clearStaleTokens();
    captureMessage("Auth token validation failed", {
      contexts: { auth: { token: authToken } },
    });
    logout();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: getUser wont change
  useEffect(() => {
    if (!authToken) {
      return;
    }

    (async () => {
      try {
        const { data } = await getUser({ variables: { idToken: authToken } });
        if (!data?.getOrCreateUser) {
          return handleAuthError();
        }

        user(data.getOrCreateUser.user);
        identifyUser(data.getOrCreateUser.user);

        if (!data.getOrCreateUser.user.isPublic) {
          setIsOpen(true);
        }
      } catch (e) {
        captureException(e);
      }
    })();
  }, [authToken]);

  useEffect(() => {
    if (
      "invite_code" in search &&
      typeof search.invite_code === "string" &&
      !!search.invite_code
    ) {
      sessionStorage.setItem(INVITE_CODE_STORAGE_KEY, search.invite_code);
    }
  }, [search]);

  if (!isOpen) {
    return null;
  }

  return (
    <WrapperModal
      hideCloseButton={step === STEPS.DETAILS}
      onOpenChange={handleClose}
      open={isOpen}
      overlayClassName="md:items-center"
      title="Setup your profile"
    >
      <SignUp
        onComplete={handleCompleteSignup}
        onUsername={handleUsername}
        step={step}
      />
    </WrapperModal>
  );
}
